<template>
  <div class="home" :class="{ 'ie-eleven': isIE11 }">
    <h1 class="sr-only">Find Fast Electric Vehicle Charging</h1>
    <section class="secondaryBG">
      <HeroWithContentOverlay :maxWidth="1920" fullWidthMobile :overlayBG="false">
        <template #image>
          <div class="img-container">
            <div class="right-arrow">
              <button @click="nextContent">
                <svg class="nav-icon" viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path
                    d="M35 20 L70 50 L35 80"
                    stroke="currentColor"
                    stroke-width="15"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
              </button>
            </div>
            <img
              v-if="!isPhone"
              class="slide-image"
              :src="slides[currentIndex].image"
              :alt="slides[currentIndex].alt"
            />
            <img v-else class="slide-image" :src="slides[currentIndex].mobileImage" :alt="slides[currentIndex].alt" />
          </div>
        </template>
        <div v-if="currentIndex === 0">
          <p v-if="isPhone" class="location-search-details">Find the nearest Electrify America EV Charging Station.</p>
          <br v-if="!isPhone" />
          <br v-if="!isPhone" />
          <br v-if="!isPhone" />
          <form
            id="homepage-location-search"
            name="homepage-location-search"
            class="search-container"
            @submit.prevent="goToLocate()"
            role="search"
          >
            <input class="search-input" placeholder="ENTER CITY OR ZIP" v-model="searchParam" type="search" />
            <button class="green-circle" aria-label="Submit" type="submit" />
          </form>
        </div>
        <div v-if="currentIndex === 1">
          <div v-if="!isPhone">
            <br />
            <br />
            <br />
          </div>
          <br />
          <br />
          <br />
          <router-link :to="{ name: 'charge-on-en', query: { utm_source: 'homepage' } }">
            <button class="button button-white">Get Started</button>
          </router-link>
        </div>
      </HeroWithContentOverlay>
      <section class="first-charge-container">
        <div class="header-content">
          <h1 class="title">Charge First</h1>
          <p class="description">
            More chargers, more freedom. Since 2016, Electrify America has made EV charging more accessible. Now, we’re
            powering your EV journey as the largest open Hyper-Fast charging network in the U.S.
          </p>
        </div>
        <div class="stats-wrapper">
          <div class="stat-card">
            <div class="icon-wrapper">
              <img src="../assets/images/Home/charger-icon.png" alt="Charging Icon" />
            </div>
            <div class="stat-info">
              <span class="stat-number">6,200+</span>
              <p class="stat-label">Hyper-Fast EV Chargers<br />in North America</p>
            </div>
          </div>

          <div class="stat-card">
            <div class="icon-wrapper">
              <img src="../assets/images/Home/station-icon.png" alt="Location Icon" />
            </div>
            <div class="stat-info">
              <span class="stat-number">1,200+</span>
              <p class="stat-label">EV Charging Stations<br />in North America</p>
            </div>
          </div>
        </div>
      </section>
    </section>
    <section class="home__container grey-bg">
      <div class="middle-section">
        <h1 class="title">Charge Easy</h1>
        <p>
          Hundreds of thousands of EV owners rely on our coast-to-coast network every week. No matter if you're heading
          to work, running errands, or road-tripping across the country, you can count on us to keep you moving. Plug
          in. Power up. Go.
        </p>
        <div class="l-three-col l-three-col--tablet-two">
          <HomepageCard
            path="mobile-app-en"
            label="Mobile App "
            :src="require('@/assets/images/Home/mobile-app.png')"
            alt="Find Electrify America charging stations using your smart phone"
            tileText="Mobile app tile"
          >
            <p>Simplify the charging experience with the Electrify America app.</p>
            <a
              class="homepage-card__cta"
              rel="noopener"
              target="_blank"
              href="https://itunes.apple.com/us/app/electrify-america/id1458030456?mt=8"
            >
              <img
                loading="lazy"
                alt="Download on the App Store"
                :src="require('@/assets/images/Global/app-store-logo@2x.webp')"
              />
            </a>
            <a
              class="homepage-card__cta"
              rel="noopener"
              target="_blank"
              href="https://play.google.com/store/apps/details?id=com.ea.evowner&rdid=com.ea.evowner"
            >
              <img
                loading="lazy"
                alt="Get it on Google Play"
                :src="require('@/assets/images/Global/google-play-badge@2x.webp')"
              />
            </a>
          </HomepageCard>
          <HomepageCard
            path="charging-your-ev-en"
            label="Charging 101"
            :src="require('@/assets/images/Home/charging101.png')"
            alt="Person plugging an EV charger into their vehicle"
            tileText="Charging 101 tile"
          >
            <p>We'll guide you through the charging process from beginning to end until you become a pro.</p>
          </HomepageCard>
          <HomepageCard
            path="pricing-en"
            label="Pass Plus Membership"
            :src="require('@/assets/images/Home/passplus.png')"
            alt="A picture of the renewable energy farm. A sign says Solar Glow 1."
            tileText="Easily save with Pass+"
          >
            <p>Become a Pass+ member to save about 25% on charging.</p>
          </HomepageCard>
        </div>
      </div>
    </section>
    <section class="home__container">
      <div class="middle-section">
        <h1 class="title">Charge Forward</h1>
        <p>
          Built for today, ready for tomorrow — Electrify America is leading the way with more speed, a dedicated
          testing facility, and smarter tech, like our award-winning deployment of battery energy storage systems
          <a class="link" href="https://media.electrifyamerica.com/releases/270" target="_blank">(BESS)</a>.
        </p>
        <div class="l-three-col l-three-col--tablet-two">
          <HomepageCard
            url="https://bit.ly/network-upgrades-and-maintenance"
            label="Network Transparency"
            :src="require('@/assets/images/Home/network-transparency.png')"
            alt="A large Electriy America charging station in a parking lot"
            tileText="Network Transparency"
          >
            <p>Stay in the loop of current and planned maintenance so you can plan accordingly.</p>
          </HomepageCard>
          <HomepageCard
            path="renewable-energy-en"
            label="Renewable Energy"
            :src="require('@/assets/images/Home/renewable-energy.png')"
            alt="Large solar energy field in a desert with bright sunlight"
            tileText="Renewable Energy"
          >
            <p>
              We're building a grid-friendly charging network through Electrify America Solar Glow<sup>TM</sup> 1 and
              other initiatives.
            </p>
          </HomepageCard>
          <HomepageCard
            url="https://media.electrifyamerica.com/en-us/"
            label="Latest Updates"
            :src="require('@/assets/images/Home/latest-updates.png')"
            alt="Woman looking at her phone."
            tileText="Latest Updates"
          >
            <p>Read about our latest innovations, which power today and innovate tomorrow.</p>
          </HomepageCard>
        </div>
      </div>
      <div class="green-wave-container">
        <div class="content-wrapper">
          <div class="card">
            <div class="card-text top-card">
              <h2 class="card-heading">Host or Own a Station</h2>
              <p class="card-body">
                Whether you want to own or host a station, we'll power the entire journey for you.
              </p>
              <a href="/business-solutions/" class="learn-more"> LEARN MORE <span class="arrow">→</span> </a>
            </div>
            <div class="card-image-container">
              <img
                src="@/assets/images/Home/footer_charger.png"
                alt="Electrify America charger in use"
                class="card-image"
              />
            </div>
          </div>

          <div class="card">
            <div class="card-image-container">
              <img src="@/assets/images/Home/footer_mirror.png" alt="Happy man in a car" class="card-image" />
            </div>
            <div class="card-text">
              <h2 class="card-heading">We're Helping Communities Charge On</h2>
              <p class="card-body">
                Whether you charge with us frequently or only during road trips, our mission is to make your charge as
                seamless possible, so can can plug in, power up, and go.
              </p>
              <a href="/about-us/" class="learn-more"> ABOUT US <span class="arrow">→</span> </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import AOS from 'aos';
import HomepageCard from '@/components/HomepageCard/HomepageCard.vue';
import HeroWithContentOverlay from '@/components/HeroWithContentOverlay/HeroWithContentOverlay.vue';

export default {
  name: 'home',
  components: {
    HomepageCard,
    HeroWithContentOverlay,
  },
  metaInfo: {
    title: 'EV Charging Stations | Electrify America',
    meta: [
      {
        name: 'description',
        content:
          "Charge your EV in as little as 30 minutes with Electrify America, the nation's largest open Hyper-Fast charging network. Find a station near you.",
      },
    ],
    link: [{ rel: 'canonical', href: 'https://www.electrifyamerica.com/' }],
    script: [
      {
        type: 'application/ld+json',
        json: {
          '@context': 'https://schema.org',
          '@graph': [
            {
              '@type': 'Organization',
              '@id': 'https://www.electrifyamerica.com/#organization',
              name: 'Electrify America',
              url: 'https://www.electrifyamerica.com/',
              logo: 'https://www.electrifyamerica.com/img/logo.2081be3c.png',
              description: 'Electrify America operates the largest open Hyper-Fast charging network in the U.S.',
              sameAs: [
                'https://www.instagram.com/electrifyamerica/',
                'https://www.tiktok.com/@electrifyamerica',
                'https://www.linkedin.com/company/electrifyamerica/',
                'https://x.com/electrifyam',
                'https://www.facebook.com/ElectrifyAmerica/',
                'https://www.youtube.com/channel/UC3GGcyuzYYyYmZ0hJKlBb6w',
              ],
              contactPoint: {
                '@type': 'ContactPoint',
                telephone: '+1-833-632-2778',
                contactType: 'customer service',
                areaServed: 'US',
              },
            },
            {
              '@type': 'WebSite',
              '@id': 'https://www.electrifyamerica.com/#website',
              url: 'https://www.electrifyamerica.com/',
              name: 'Electrify America',
              publisher: { '@id': 'https://www.electrifyamerica.com/#organization' },
            },
          ],
        },
      },
    ],
  },
  data() {
    return {
      searchParam: null,
      currentIndex: 0,
      timer: null,
      slides: [
        {
          id: 1,
          image: require('@/assets/images/Home/mainImg.jpg'),
          mobileImage: require('@/assets/images/Home/mainImg_phone.png'),
          alt: 'Charge On - When and where you need',
        },
        {
          id: 2,
          image: require('@/assets/images/Home/mainImg2.jpg'),
          mobileImage: require('@/assets/images/Home/mainImg2_phone.png'),
          alt: 'Share your story - show us how you charge',
        },
      ],
    };
  },
  methods: {
    goToLocate() {
      if (this.searchParam) {
        const zoom = window.innerWidth >= 1200 ? 5 : 4;

        this.$store.commit('map/resetFilters');
        this.$store.commit('map/setIsFilterPanelExpanded', false);
        this.$store.commit('map/setIsStationDetailExpanded', false);
        this.$store.commit('map/setIsStationListExpanded', false);
        this.$store.commit('locations/setUniqueLocation', null);
        this.$store.commit('locations/setUniqueRoute', null);
        this.$store.dispatch('map/updateMapZoomAndCenter', {
          zoom,
          center: { lat: 38, lng: -96 },
        });
        this.$router.push({ name: 'locate-charger-en', query: { search: this.searchParam } });
      } else {
        this.$router.push({ name: 'locate-charger-en' });
      }
    },
    nextContent() {
      this.currentIndex = (this.currentIndex + 1) % this.slides.length;
      this.startRotation();
    },
    startRotation() {
      clearInterval(this.timer);
      this.timer = setInterval(() => {
        if (!document.activeElement.classList.contains('search-input')) {
          this.currentIndex = (this.currentIndex + 1) % this.slides.length;
        }
      }, 7000);
    },
  },
  computed: {
    isIE11() {
      return navigator.userAgent.toUpperCase().indexOf('TRIDENT/') !== -1;
    },
    isPhone() {
      return this.$resize && this.$mq.below(750);
    },
  },
  mounted() {
    setTimeout(() => {
      AOS.refresh();
    }, 300);
    this.startRotation();
  },
  watch: {
    searchParam() {
      if (this.searchParam === null || this.searchParam === '') {
        this.startRotation();
      } else {
        clearInterval(this.timer);
      }
    },
  },
};
</script>

<style scoped lang="scss">
.home {
  padding: 0px 0 60px 0;
  display: flex;
  flex-direction: column;
  font-weight: $fw-regular;
  position: relative;

  @media (min-width: 750px) {
    padding-bottom: 0;
  }
  &__container {
    padding-bottom: 60px;
    @media only screen and (min-width: 750px) {
      padding: 0 40px 60px;
    }
  }
  .l-three-col {
    justify-content: flex-start;
  }
  .apple {
    width: 124.5px !important;
  }
  .google-play {
    width: 134px;
    margin-left: 10px;
    height: 40px;
  }
}
.secondaryBG {
  @media (min-width: 750px) {
    padding: 36px 36px 52px;
  }
}

.disclaimer {
  line-height: 1;
  font-size: 13px;
  padding-top: 30px;
}

.middle-section {
  padding: 60px 20px 30px 20px;
  display: flex;
  flex-direction: column;
  max-width: 1400px;
  margin: 0 auto;
  z-index: 2;

  @media (min-width: 950px) {
    padding: 60px 100px 44px 100px;
  }

  @media (min-width: 750px) {
    padding: 60px 50px 44px 50px;
  }

  h3 {
    letter-spacing: 0.02em;
    line-height: 36px;
    font-weight: 600;
    max-width: 504px;
    font-size: 24px;
    margin-bottom: 60px;

    @media only screen and (min-width: 1130px) {
      font-size: 32px;
    }
    @media only screen and (max-width: 750px) {
      font-size: 20px;
      line-height: 34px;
      width: 100%;
      margin: 0 auto 34px;
      max-width: 600px;
    }
  }

  .middle-grid {
    column-gap: 40px;
    display: flex;
    flex-wrap: wrap;
    row-gap: 60px;
    margin: 0;
    padding-bottom: 42px;

    @media (min-width: 750px) {
      padding: 60px 50px 44px;
    }
  }
}
.hero-with-overlay {
  h3 {
    letter-spacing: 2px;
    margin: 0;
    margin-top: 10px;
  }
}

.secondaryBG {
  .hero-with-overlay {
    h1 {
      font-weight: $fw-regular;
      color: $c-primary-background;
      @media (min-width: 750px) {
        font-size: 40px;
      }
      @media (min-width: 1030px) {
        font-size: 48px;
      }
      span {
        font-style: italic;
        font-weight: $fw-medium;
      }
    }
    ::v-deep &__content {
      top: 68%;
      @media (min-width: 750px) {
        top: 55%;
      }
    }
    p {
      @media (min-width: 1030px) {
        font-size: 24px;
        letter-spacing: 1.2px;
      }
    }
  }
}

.first-charge-container {
  max-width: 1400px;
  margin: 0 auto;

  @media (max-width: 900px) {
    width: 85%;
  }
  padding: 40px 20px;
  padding-top: 60px;
}

.header-content {
  margin-bottom: 60px;
}

.title {
  font-weight: 800;
  font-style: italic;
}

/* Stats Section Layout */
.stats-wrapper {
  display: flex;
  justify-content: flex-start;
  align-items: center;
  gap: 40px;
  flex-wrap: wrap;
}

.stat-card {
  display: flex;
  align-items: center;
  gap: 20px;
  flex-grow: 1;
}

.icon-wrapper img {
  width: 100px;
  height: 100px;
  object-fit: contain;
  display: block;
  @media (max-width: 900px) {
    width: 50px;
    height: 50px;
  }
}

.stat-info {
  display: flex;
  flex-direction: row;
  justify-content: center;
}

.stat-number {
  font-size: 3.5rem;
  font-weight: 800;
  font-style: italic;
  line-height: 1;
  align-content: center;
  color: $c-primary-light;
}

.stat-label {
  margin: 0;
  padding-left: 20px;
}

/* Mobile Responsiveness */
@media (max-width: 900px) {
  .stats-wrapper {
    flex-direction: column;
    align-items: flex-start;
    gap: 40px;
  }

  .title {
    font-size: 2.5rem;
  }

  .stat-number {
    font-size: 1.5rem;
  }
}

@media (max-width: 750px) {
  .location-search-details {
    padding-top: 75px;
  }
}

.grey-bg {
  background-color: $c-secondary-background;
}

.search-container {
  margin-top: 30px;
  background-color: $c-primary-background;
  border-radius: 100px;
  width: 327px;
  height: 55px;
  position: relative;
  @media only screen and (min-width: 750px) {
    width: 375px;
  }
  .search-input {
    position: absolute;
    top: 16px;
    left: 20px;
    height: 26px;
    border: none;
    font-weight: $fw-medium;
    font-size: 13px;
    width: 200px;
  }
  .green-circle {
    width: 40px;
    height: 40px;
    position: absolute;
    right: 10px;
    border-radius: 100px;
    border: solid 1px $c-highlights;
    background: $c-highlights url('~@/assets/images/Home/Union.svg?external') no-repeat center left 16px;
    top: 8px;
    cursor: pointer;
    &:hover {
      background-color: $c-focus;
      border: 0;
    }
  }

  input[type='search'] {
    padding: 0;
  }
}

// ie11 fixes
.ie-eleven {
  .middle-section {
    width: 100%;
  }

  @media only screen and (min-width: 750px) {
    .middle-section {
      min-height: 982px;
    }
  }
}

.green-wave-container {
  display: flex;
  justify-content: center;
  padding: 40px 20px;

  background-image: url('~@/assets/images/Home/footer_background.png');
  background-size: cover;
  background-position: center;
}

.content-wrapper {
  max-width: 1400px;
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 40px; /* Vertical space between the cards */
}

.card {
  display: flex;
  align-items: center;
  padding: 40px;
  border-radius: 12px;
  gap: 40px;
}
.card-text {
  max-width: 40%;
  margin: auto;
}

.top-card {
  height: 50%;
}

.card-heading {
  color: white;
}

.card-body {
  font-size: 1rem;
  line-height: 1.5;
  margin-bottom: 20px;
  color: white;
}

.learn-more {
  font-weight: 600;
  color: white;
}

.arrow {
  font-size: 1.2rem;
}

.card-image-container {
  width: 100%;
  max-width: 45%;
  border-radius: 8px;
}

.card-image {
  width: 100%;
}

@media (max-width: 850px) {
  .green-wave-container {
    background-image: url('~@/assets/images/Home/footer_background_mobile.png');
  }
  .card {
    flex-direction: column;
    padding: 30px;
  }

  .card-image-container {
    max-width: 100%;
  }

  .card-heading {
    font-size: 1.5rem;
  }

  .card-text {
    order: 1;
    max-width: 100%;
  }
}
.img-container {
  width: 100%;
  height: 100%;
  .right-arrow {
    position: absolute;
    right: 5%;
    top: 45%;
    z-index: 100;
    button {
      color: white;
      border: solid 2px white;
      border-radius: 5px;
      background-color: $c-primary-light;
      width: 35px;
      height: 35px;
      cursor: pointer;
      display: flex;
      justify-content: center;
      align-items: center;
      transition: background-color 0.3s ease, transform 0.1s ease;

      @media (max-width: 600px) {
        width: 30px;
        height: 30px;
        font-size: 1em;
      }

      &:hover {
        background-color: #aeaeae;
        transform: scale(1.05);
      }
    }
  }
}
.slide-image {
  width: 100%;
  height: 100%;
  border-radius: 10px;
}

.button-white {
  padding-bottom: 1.28em !important;
}
</style>
